import { useStore } from '@nanostores/react';
import { useState } from 'react';
import { DeleteAllChatsDialog } from '~/components/dialogs/delete-all-chats';
import { DangerZoneWrapper } from '~/components/dialogs/settings/DangerZone';
import { SectionContainer } from '~/components/dialogs/settings/SectionContainer';
import { SettingBlock } from '~/components/dialogs/settings/SettingBlock';
import { Button } from '~/components/ui/Button';
import { Select } from '~/components/ui/Select';
import { Switch } from '~/components/ui/Switch';
import { settingsService } from '~/lib/services/settings-service';
import { chatStore } from '~/lib/stores/chat';
import { editorSettingsStore } from '~/lib/stores/settings';
import { themeOptionStore, setTheme as updateAppTheme } from '~/lib/stores/theme';
import type { ThemeOption } from '~/types/theme';

const themeDropdownOptions: { label: string; value: ThemeOption }[] = [
  {
    label: 'Light',
    value: 'light',
  },
  {
    label: 'Dark',
    value: 'dark',
  },
  {
    label: 'System',
    value: 'system',
  },
];

export const General = () => {
  const [showDeleteAllChatsDialog, setShowDeleteAllChatsDialog] = useState(false);
  const forceShowTokenUsage = useStore(chatStore.forceShowTokenInChat);
  const theme = useStore(themeOptionStore);
  const editorSettings = useStore(editorSettingsStore);

  return (
    <SectionContainer title="General" className="gap-10">
      <div className="flex flex-col gap-6">
        <div className="text-bolt-ds-textTertiary text-sm font-semibold">Appearance</div>
        <SettingBlock
          title="Theme"
          id="theme-switch"
          className="w-4/5"
          description="Change the interface to light, dark or your default system preference."
          action={
            <Select
              id="theme-switch"
              className="text-bolt-ds-textSecondary ml-auto"
              containerClassName="w-[125px]"
              placeholder="Select"
              options={themeDropdownOptions}
              defaultValue={theme}
              onValueChange={(value) => {
                const themeOption = value as ThemeOption;
                updateAppTheme(themeOption);
              }}
            />
          }
        />
        <SettingBlock
          id="display-token-usage"
          title="Display token usage in chat"
          className="w-5/6"
          description="When activated, your token consumption will be shown above the prompt field at all times."
          action={
            <Switch
              id="display-token-usage"
              checked={forceShowTokenUsage}
              onCheckedChange={(value) => {
                settingsService.set('general', 'showTokenUsageInChat', value);
              }}
            />
          }
        />
      </div>
      <div className="flex flex-col gap-4">
        <div className="text-bolt-ds-textTertiary text-sm font-semibold">Code</div>
        <SettingBlock
          id="editor-line-wrapping"
          title="Editor line wrapping"
          className="w-4/5"
          description="When enabled, long lines of code will visually wrap to the next line within the editor, preventing
              horizontal scrolling."
          action={
            <Switch
              id="editor-line-wrapping"
              checked={editorSettings.lineWrapping}
              onCheckedChange={(value) => {
                settingsService.set('editor', 'lineWrapping', value);
              }}
            />
          }
        />
      </div>

      <DangerZoneWrapper>
        <SettingBlock
          wrapperClassName="w-full"
          id="delete-chat-history"
          title="Delete all chat history"
          description="Deleting your messaging history will erase all messages but keep your projects."
          action={
            <Button
              id="delete-chat-history"
              size="md"
              variant="link-color"
              color="danger"
              onClick={() => setShowDeleteAllChatsDialog(true)}
            >
              Delete chat history
            </Button>
          }
        />
        <DeleteAllChatsDialog open={showDeleteAllChatsDialog} onClose={() => setShowDeleteAllChatsDialog(false)} />
      </DangerZoneWrapper>
    </SectionContainer>
  );
};
